/**
 * Security utility functions for validating and sanitizing inputs
 * Prevents injection attacks and ensures data integrity
 *
 * @module securityHelpers
 */

/**
 * Validate UUID format (commonly used for IDs in GraphQL/DynamoDB)
 * @param {string} id - ID to validate
 * @returns {boolean} - True if valid UUID format
 */
export const isValidUUID = id => {
  if (!id || typeof id !== 'string') return false;

  // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

/**
 * Validate alphanumeric ID with optional hyphens
 * @param {string} id - ID to validate
 * @param {number} maxLength - Maximum allowed length
 * @returns {boolean} - True if valid
 */
export const isValidAlphanumericId = (id, maxLength = 100) => {
  if (!id || typeof id !== 'string') return false;
  if (id.length > maxLength) return false;

  // Only alphanumeric characters and hyphens
  const alphanumericRegex = /^[a-zA-Z0-9-]+$/;
  return alphanumericRegex.test(id);
};

/**
 * Validate and sanitize GraphQL variables to prevent injection
 * @param {Object} variables - GraphQL query variables
 * @returns {Object} - Sanitized variables or throws error
 */
export const validateGraphQLVariables = variables => {
  if (!variables || typeof variables !== 'object') {
    throw new Error('Invalid GraphQL variables');
  }

  const sanitized = {};

  for (const [key, value] of Object.entries(variables)) {
    // Skip null/undefined
    if (value === null || value === undefined) {
      sanitized[key] = value;
      continue;
    }

    // Handle strings - check for injection attempts
    if (typeof value === 'string') {
      // Check for GraphQL injection patterns
      const dangerousPatterns = [
        /\{.*\}/, // GraphQL query injection
        /\$\w+/, // Variable injection
        /__typename/i, // GraphQL introspection
        /mutation/i, // Mutation injection
        /subscription/i, // Subscription injection
      ];

      const hasDangerousPattern = dangerousPatterns.some(pattern => pattern.test(value));

      if (hasDangerousPattern) {
        console.warn(`[Security] Suspicious pattern detected in GraphQL variable: ${key}`);
        throw new Error(`Invalid input format for ${key}`);
      }

      sanitized[key] = value;
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * Validate URL parameter before using in GraphQL queries
 * @param {string} param - URL parameter value
 * @param {string} paramName - Name of the parameter (for error messages)
 * @param {Object} options - Validation options
 * @returns {string} - Validated parameter or throws error
 */
export const validateUrlParam = (param, paramName = 'parameter', options = {}) => {
  const { required = true, maxLength = 100, allowUUID = true, allowAlphanumeric = true } = options;

  // Check if required
  if (required && (!param || param.trim() === '')) {
    throw new Error(`${paramName} is required`);
  }

  // If not required and empty, return empty string
  if (!required && !param) {
    return '';
  }

  // Check length
  if (param.length > maxLength) {
    throw new Error(`${paramName} exceeds maximum length of ${maxLength}`);
  }

  // Validate format
  let isValid = false;

  if (allowUUID && isValidUUID(param)) {
    isValid = true;
  } else if (allowAlphanumeric && isValidAlphanumericId(param, maxLength)) {
    isValid = true;
  }

  if (!isValid) {
    console.warn(`[Security] Invalid ${paramName} format: ${param}`);
    throw new Error(`Invalid ${paramName} format`);
  }

  return param;
};

/**
 * Sanitize data before storing in localStorage
 * @param {string} key - localStorage key
 * @param {any} value - Value to store
 * @returns {boolean} - Success status
 */
export const safeLocalStorageSet = (key, value) => {
  try {
    // Validate key
    if (!key || typeof key !== 'string' || key.length > 100) {
      console.error('[Security] Invalid localStorage key');
      return false;
    }

    // Validate value size (localStorage has 5-10MB limit)
    const valueStr = typeof value === 'string' ? value : JSON.stringify(value);
    const sizeInMB = new Blob([valueStr]).size / (1024 * 1024);

    if (sizeInMB > 2) {
      console.error('[Security] localStorage value too large:', sizeInMB, 'MB');
      return false;
    }

    localStorage.setItem(key, valueStr);
    return true;
  } catch (error) {
    console.error('[Security] localStorage error:', error);
    return false;
  }
};

/**
 * Safely retrieve and parse JSON from localStorage
 * @param {string} key - localStorage key
 * @param {any} defaultValue - Default value if key doesn't exist
 * @returns {any} - Parsed value or default
 */
export const safeLocalStorageGet = (key, defaultValue = null) => {
  try {
    const value = localStorage.getItem(key);
    if (!value) return defaultValue;

    // Try to parse as JSON
    try {
      return JSON.parse(value);
    } catch {
      // Return as string if not valid JSON
      return value;
    }
  } catch (error) {
    console.error('[Security] localStorage retrieval error:', error);
    return defaultValue;
  }
};

/**
 * Validate file upload to prevent malicious uploads
 * @param {File} file - File object
 * @param {Object} options - Validation options
 * @returns {Object} - { isValid: boolean, error?: string }
 */
export const validateFileUpload = (file, options = {}) => {
  const {
    allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
    maxSize = 10 * 1024 * 1024, // 10MB default
    allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif'],
  } = options;

  if (!file) {
    return { isValid: false, error: 'No file provided' };
  }

  // Check file size
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: `File too large. Maximum size: ${maxSize / (1024 * 1024)}MB`,
    };
  }

  // Check MIME type
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `Invalid file type. Allowed: ${allowedTypes.join(', ')}`,
    };
  }

  // Check file extension
  const extension = file.name
    .split('.')
    .pop()
    ?.toLowerCase();
  if (!extension || !allowedExtensions.includes(extension)) {
    return {
      isValid: false,
      error: `Invalid file extension. Allowed: ${allowedExtensions.join(', ')}`,
    };
  }

  // Check for double extensions (e.g., file.jpg.exe)
  const parts = file.name.split('.');
  if (parts.length > 2) {
    return {
      isValid: false,
      error: 'Multiple file extensions not allowed',
    };
  }

  return { isValid: true };
};
